<template>
  <b-form class="grc-form" @submit.prevent="submitForm">
    <b-alert
      v-if="localForm.closureApprover && approvalLabel"
      show
      variant="info"
      class="grc-form__alert"
    >
      Encerramento: {{ approvalLabel }}
    </b-alert>

    <b-alert
      v-if="submitValidationError"
      show
      variant="warning"
      class="grc-form__alert py-2"
    >
      {{ submitValidationError }}
    </b-alert>

    <section class="grc-form__section">
      <div class="grc-form__section-header">
        <h3 class="grc-form__section-title">Identificação e objetivo</h3>
        <p class="grc-form__section-description">
          Defina a ação, o resultado esperado e o racional da execução.
        </p>
      </div>

      <b-row>
        <b-col cols="12" md="8">
          <b-form-group label="Título da ação *">
            <b-form-input
              v-model.trim="localForm.title"
              required
            ></b-form-input>
          </b-form-group>
        </b-col>
        <b-col cols="12" md="4">
          <b-form-group label="Status *">
            <b-form-select
              v-model="localForm.status"
              :options="statusOptions"
            ></b-form-select>
          </b-form-group>
        </b-col>
      </b-row>

      <b-form-group label="Descrição">
        <b-form-textarea
          v-model.trim="localForm.description"
          rows="2"
        ></b-form-textarea>
      </b-form-group>

      <b-row>
        <b-col cols="12" md="6">
          <b-form-group label="O que será feito *">
            <b-form-textarea
              v-model.trim="localForm.whatWillBeDone"
              rows="3"
              required
            ></b-form-textarea>
          </b-form-group>
        </b-col>
        <b-col cols="12" md="6">
          <b-form-group label="Por que será feito">
            <b-form-textarea
              v-model.trim="localForm.whyWillBeDone"
              rows="3"
            ></b-form-textarea>
          </b-form-group>
        </b-col>
      </b-row>
    </section>

    <section class="grc-form__section">
      <div class="grc-form__section-header">
        <h3 class="grc-form__section-title">Origem e contexto</h3>
        <p class="grc-form__section-description">
          Organize a origem da ação e o contexto em que ela será executada.
        </p>
      </div>

      <b-row>
        <b-col cols="12" md="4">
          <b-form-group label="Tipo de ação *">
            <b-form-select
              v-model="localForm.actionType"
              :options="actionTypeOptions"
            ></b-form-select>
          </b-form-group>
        </b-col>
        <b-col cols="12" md="4">
          <b-form-group label="Fonte *">
            <b-form-input
              v-model.trim="localForm.sourceName"
              required
            ></b-form-input>
          </b-form-group>
        </b-col>
        <b-col cols="12" md="4">
          <b-form-group label="Tipo de fonte *">
            <b-form-select
              v-model="localForm.sourceType"
              :options="sourceTypeOptions"
            ></b-form-select>
          </b-form-group>
        </b-col>
      </b-row>

      <b-row>
        <b-col cols="12" md="4">
          <b-form-group label="Identificador da fonte">
            <b-form-input
              v-model.trim="localForm.sourceIdentifier"
            ></b-form-input>
          </b-form-group>
        </b-col>
        <b-col cols="12" md="4">
          <b-form-group label="Jurisdição">
            <b-form-input
              v-model.trim="localForm.jurisdiction"
              :required="localForm.sourceType === 'legal-regulatory'"
            ></b-form-input>
          </b-form-group>
        </b-col>
        <b-col cols="12" md="4">
          <b-form-group label="Núcleo/Categoria interna">
            <b-form-input
              v-model.trim="localForm.internalCategory"
            ></b-form-input>
          </b-form-group>
        </b-col>
      </b-row>

      <b-row>
        <b-col cols="12" md="4">
          <b-form-group label="Área *">
            <b-form-select
              v-model="localForm.area"
              :options="areaOptions"
              required
            ></b-form-select>
          </b-form-group>
        </b-col>
        <b-col cols="12" md="4">
          <b-form-group label="Escopo">
            <b-form-select
              v-model="localForm.scope"
              :options="scopeOptionsWithEmpty"
            ></b-form-select>
          </b-form-group>
        </b-col>
        <b-col cols="12" md="4">
          <b-form-group label="Prioridade">
            <b-form-select
              v-model="localForm.priority"
              :options="priorityOptions"
            ></b-form-select>
          </b-form-group>
        </b-col>
      </b-row>

      <b-form-group label="Projetos vinculados">
        <SearchableSelect
          v-model="localForm.projects"
          :options="projectOptions"
          multiple
          placeholder="Selecione um ou mais projetos"
          search-placeholder="Buscar projeto..."
          :use-company-filter="false"
          :control-class="'form-control'"
        />
      </b-form-group>
    </section>

    <section class="grc-form__section">
      <div class="grc-form__section-header">
        <h3 class="grc-form__section-title">Responsáveis e rastreabilidade</h3>
        <p class="grc-form__section-description">
          Conecte participantes, aprovadores e itens rastreáveis relacionados à
          ação.
        </p>
      </div>

      <b-row>
        <b-col cols="12" md="4">
          <b-form-group label="Responsável">
            <b-form-select
              v-model="localForm.owner"
              :options="userOptionsWithEmpty"
            ></b-form-select>
          </b-form-group>
        </b-col>
        <b-col cols="12" md="4">
          <b-form-group label="Coparticipantes">
            <SearchableSelect
              v-model="localForm.coparticipants"
              :options="userOptions"
              multiple
              placeholder="Selecione um ou mais participantes"
              search-placeholder="Buscar participante..."
              :use-company-filter="false"
              :control-class="'form-control'"
            />
          </b-form-group>
        </b-col>
        <b-col cols="12" md="4">
          <b-form-group label="Aprovador de encerramento">
            <b-form-select
              v-model="localForm.closureApprover"
              :options="userOptionsWithEmpty"
            ></b-form-select>
          </b-form-group>
        </b-col>
      </b-row>

      <b-row>
        <b-col cols="12" md="6">
          <b-form-group label="Requisitos vinculados">
            <SearchableSelect
              v-model="localForm.requirements"
              :options="requirementOptions"
              multiple
              placeholder="Selecione um ou mais requisitos"
              search-placeholder="Buscar requisito..."
              :use-company-filter="false"
              :control-class="'form-control'"
            />
          </b-form-group>
        </b-col>
        <b-col cols="12" md="6">
          <b-form-group label="Riscos vinculados">
            <SearchableSelect
              v-model="localForm.risks"
              :options="riskOptions"
              multiple
              placeholder="Selecione um ou mais riscos"
              search-placeholder="Buscar risco..."
              :use-company-filter="false"
              :control-class="'form-control'"
            />
          </b-form-group>
        </b-col>
      </b-row>

      <b-form-group label="Controles vinculados">
        <SearchableSelect
          v-model="localForm.controls"
          :options="controlOptions"
          multiple
          placeholder="Selecione um ou mais controles"
          search-placeholder="Buscar controle..."
          :use-company-filter="false"
          :control-class="'form-control'"
        />
      </b-form-group>

      <b-form-group label="Critérios de cumprimento">
        <b-form-textarea
          v-model.trim="localForm.complianceCriteria"
          rows="2"
        ></b-form-textarea>
      </b-form-group>
    </section>

    <section class="grc-form__section">
      <div class="grc-form__section-header">
        <h3 class="grc-form__section-title">Execução e recursos</h3>
        <p class="grc-form__section-description">
          Acompanhe prazo, progresso, checklist, recursos e aprendizados da
          ação.
        </p>
      </div>

      <b-row>
        <b-col cols="12" md="3">
          <b-form-group label="Início">
            <b-form-input
              v-model="localForm.startDate"
              type="date"
            ></b-form-input>
          </b-form-group>
        </b-col>
        <b-col cols="12" md="3">
          <b-form-group label="Prazo">
            <b-form-input
              v-model="localForm.dueDate"
              type="date"
            ></b-form-input>
          </b-form-group>
        </b-col>
        <b-col cols="12" md="3">
          <b-form-group label="Progresso (%)">
            <b-form-input
              v-model="localForm.progress"
              type="number"
              min="0"
              max="100"
            ></b-form-input>
          </b-form-group>
        </b-col>
        <b-col cols="12" md="3">
          <b-form-group label="Motivo da atualização">
            <b-form-textarea
              v-model.trim="localForm.lastUpdateReason"
              rows="2"
            ></b-form-textarea>
          </b-form-group>
        </b-col>
      </b-row>

      <b-form-group label="Checklist/Tarefas">
        <b-form-textarea
          v-model.trim="localForm.taskChecklist"
          rows="3"
        ></b-form-textarea>
      </b-form-group>

      <b-form-group label="Marcos/Entregáveis">
        <b-form-textarea
          v-model.trim="localForm.milestones"
          rows="3"
        ></b-form-textarea>
      </b-form-group>

      <b-form-group label="Comentários e lições aprendidas">
        <b-form-textarea
          v-model.trim="localForm.commentsAndLessonsLearned"
          rows="3"
        ></b-form-textarea>
      </b-form-group>

      <b-row>
        <b-col cols="12" md="4">
          <b-form-group label="Horas">
            <b-form-input
              v-model="localForm.resourceHours"
              type="number"
              min="0"
              step="0.5"
            ></b-form-input>
          </b-form-group>
        </b-col>
        <b-col cols="12" md="4">
          <b-form-group label="Custos">
            <b-form-input
              v-model="localForm.resourceCost"
              type="number"
              min="0"
              step="0.01"
            ></b-form-input>
          </b-form-group>
        </b-col>
        <b-col cols="12" md="4">
          <b-form-group label="Sistemas envolvidos">
            <b-form-input
              v-model.trim="localForm.resourceSystems"
            ></b-form-input>
          </b-form-group>
        </b-col>
      </b-row>

      <div class="grc-form__toggle-hint">
        Defina se o plano continua ativo para acompanhamento do progresso e
        encerramento.
      </div>
      <b-form-checkbox
        v-model="localForm.active"
        switch
        class="grc-form__toggle"
      >
        Plano de ação ativo
      </b-form-checkbox>
    </section>

    <div class="grc-form__actions">
      <b-button variant="secondary" @click="$emit('cancel')">Cancelar</b-button>
      <b-button
        type="submit"
        variant="primary"
        :disabled="saving || Boolean(submitValidationError)"
      >
        {{ saving ? "Salvando..." : "Salvar" }}
      </b-button>
    </div>
  </b-form>
</template>

<script>
import SearchableSelect from "@/components/SearchableSelect.vue";

const GRCActionPlanStatus = {
  PLANNED: 1,
  IN_PROGRESS: 2,
  COMPLETED: 3,
  WAITING_APPROVAL: 4,
  SUSPENDED: 5,
};

export default {
  name: "GRCActionPlanForm",
  components: {
    SearchableSelect,
  },
  props: {
    actionPlan: { type: Object, default: null },
    areaOptions: { type: Array, default: () => [] },
    scopeOptions: { type: Array, default: () => [] },
    projectOptions: { type: Array, default: () => [] },
    requirementOptions: { type: Array, default: () => [] },
    riskOptions: { type: Array, default: () => [] },
    controlOptions: { type: Array, default: () => [] },
    userOptions: { type: Array, default: () => [] },
    saving: { type: Boolean, default: false },
  },
  data() {
    return {
      localForm: this.buildForm(this.actionPlan),
      statusValues: GRCActionPlanStatus,
    };
  },
  computed: {
    approvalLabel() {
      if (
        !this.actionPlan ||
        !this.actionPlan.approval ||
        !this.actionPlan.approval.status
      ) {
        return null;
      }
      const labels = {
        "not-required": "Sem aprovação obrigatória",
        pending: "Aguardando aprovação",
        approved: "Aprovado",
        rejected: "Rejeitado",
      };
      return (
        labels[this.actionPlan.approval.status] ||
        this.actionPlan.approval.status
      );
    },
    statusOptions() {
      const progressComplete =
        parseInt(this.localForm.progress || 0, 10) >= 100;

      return [
        this.buildStatusOption(this.statusValues.PLANNED, "Planejado"),
        this.buildStatusOption(this.statusValues.IN_PROGRESS, "Em execução"),
        this.buildStatusOption(
          this.statusValues.COMPLETED,
          "Concluído",
          progressComplete,
          "exige progresso em 100%"
        ),
        this.buildStatusOption(
          this.statusValues.WAITING_APPROVAL,
          "Aguardando aprovação",
          progressComplete && Boolean(this.localForm.closureApprover),
          "exige progresso em 100% e aprovador de encerramento"
        ),
        this.buildStatusOption(this.statusValues.SUSPENDED, "Suspenso"),
      ];
    },
    submitValidationError() {
      const startDate = this.localForm.startDate
        ? new Date(this.localForm.startDate).getTime()
        : null;
      const dueDate = this.localForm.dueDate
        ? new Date(this.localForm.dueDate).getTime()
        : null;
      const progress = parseInt(this.localForm.progress || 0, 10);

      if (
        startDate !== null &&
        dueDate !== null &&
        !Number.isNaN(startDate) &&
        !Number.isNaN(dueDate) &&
        dueDate < startDate
      ) {
        return "O prazo não pode ser anterior à data de início.";
      }

      if (
        [
          this.statusValues.COMPLETED,
          this.statusValues.WAITING_APPROVAL,
        ].includes(this.localForm.status) &&
        progress < 100
      ) {
        return "Para concluir ou enviar para aprovação, o progresso deve estar em 100%.";
      }

      if (
        this.localForm.status === this.statusValues.WAITING_APPROVAL &&
        !this.localForm.closureApprover
      ) {
        return "Para salvar como aguardando aprovação, informe o aprovador de encerramento.";
      }

      return "";
    },
    actionTypeOptions() {
      return [
        { value: "corrective", text: "Corretiva" },
        { value: "preventive", text: "Preventiva" },
        { value: "improvement", text: "Melhoria" },
        { value: "containment", text: "Contenção" },
        { value: "operational", text: "Operacional" },
      ];
    },
    sourceTypeOptions() {
      return [
        { value: "legal-regulatory", text: "Legal/Regulatório" },
        { value: "contract", text: "Contrato" },
        { value: "internal-policy", text: "Política interna" },
        { value: "technical-standard", text: "Norma técnica" },
        { value: "best-practice", text: "Boa prática" },
        { value: "judicial-decision", text: "Decisão judicial" },
        { value: "other", text: "Outro" },
      ];
    },
    priorityOptions() {
      return [
        { value: "low", text: "Baixa" },
        { value: "medium", text: "Média" },
        { value: "high", text: "Alta" },
      ];
    },
    scopeOptionsWithEmpty() {
      return [
        { value: null, text: "Selecione um escopo" },
        ...this.scopeOptions,
      ];
    },
    userOptionsWithEmpty() {
      return [
        { value: null, text: "Selecione um usuário" },
        ...this.userOptions,
      ];
    },
  },
  watch: {
    actionPlan: {
      immediate: true,
      handler(value) {
        this.localForm = this.buildForm(value);
      },
    },
  },
  methods: {
    buildStatusOption(value, text, enabled = true, disabledHint = "") {
      return {
        value,
        text,
        disabled: !enabled && this.localForm.status !== value,
        ...(disabledHint && !enabled && this.localForm.status !== value
          ? { text: `${text} (${disabledHint})` }
          : {}),
      };
    },
    buildForm(actionPlan) {
      return {
        title: actionPlan?.title || "",
        description: actionPlan?.description || "",
        whatWillBeDone: actionPlan?.whatWillBeDone || "",
        whyWillBeDone: actionPlan?.whyWillBeDone || "",
        actionType: actionPlan?.actionType || "corrective",
        sourceType: actionPlan?.sourceType || "legal-regulatory",
        sourceName: actionPlan?.sourceName || "",
        sourceIdentifier: actionPlan?.sourceIdentifier || "",
        jurisdiction: actionPlan?.jurisdiction || "",
        internalCategory: actionPlan?.internalCategory || "",
        area: actionPlan?.area?._id || actionPlan?.area || null,
        scope: actionPlan?.scope?._id || actionPlan?.scope || null,
        projects: Array.isArray(actionPlan?.projects)
          ? actionPlan.projects.map((item) => item._id || item)
          : [],
        requirements: Array.isArray(actionPlan?.requirements)
          ? actionPlan.requirements.map((item) => item._id || item)
          : [],
        risks: Array.isArray(actionPlan?.risks)
          ? actionPlan.risks.map((item) => item._id || item)
          : [],
        controls: Array.isArray(actionPlan?.controls)
          ? actionPlan.controls.map((item) => item._id || item)
          : [],
        complianceCriteria: actionPlan?.complianceCriteria || "",
        owner: actionPlan?.owner?._id || actionPlan?.owner || null,
        coparticipants: Array.isArray(actionPlan?.coparticipants)
          ? actionPlan.coparticipants.map((item) => item._id || item)
          : [],
        closureApprover:
          actionPlan?.closureApprover?._id ||
          actionPlan?.closureApprover ||
          null,
        startDate: this.formatDateForInput(actionPlan?.startDate),
        dueDate: this.formatDateForInput(actionPlan?.dueDate),
        priority: actionPlan?.priority || "medium",
        taskChecklist: actionPlan?.taskChecklist || "",
        milestones: actionPlan?.milestones || "",
        progress:
          actionPlan?.progress === 0 || actionPlan?.progress
            ? actionPlan.progress
            : 0,
        commentsAndLessonsLearned: actionPlan?.commentsAndLessonsLearned || "",
        resourceHours:
          actionPlan?.resourceHours === 0 || actionPlan?.resourceHours
            ? actionPlan.resourceHours
            : "",
        resourceCost:
          actionPlan?.resourceCost === 0 || actionPlan?.resourceCost
            ? actionPlan.resourceCost
            : "",
        resourceSystems: actionPlan?.resourceSystems || "",
        lastUpdateReason: actionPlan?.lastUpdateReason || "",
        status: actionPlan?.status || GRCActionPlanStatus.PLANNED,
        active: actionPlan?.active !== false,
      };
    },
    formatDateForInput(value) {
      if (!value) {
        return null;
      }
      return new Date(value).toISOString().slice(0, 10);
    },
    normalizeId(value) {
      if (value === null || value === undefined || value === "") {
        return null;
      }
      const parsed = parseInt(value);
      return Number.isNaN(parsed) ? null : parsed;
    },
    submitForm() {
      if (this.submitValidationError) {
        return;
      }

      this.$emit("submit", {
        ...this.localForm,
        area: this.normalizeId(this.localForm.area),
        scope: this.normalizeId(this.localForm.scope),
        projects: (this.localForm.projects || [])
          .map((item) => this.normalizeId(item))
          .filter(Boolean),
        requirements: (this.localForm.requirements || [])
          .map((item) => this.normalizeId(item))
          .filter(Boolean),
        risks: (this.localForm.risks || [])
          .map((item) => this.normalizeId(item))
          .filter(Boolean),
        controls: (this.localForm.controls || [])
          .map((item) => this.normalizeId(item))
          .filter(Boolean),
        owner: this.normalizeId(this.localForm.owner),
        coparticipants: (this.localForm.coparticipants || [])
          .map((item) => this.normalizeId(item))
          .filter(Boolean),
        closureApprover: this.normalizeId(this.localForm.closureApprover),
        progress: parseInt(this.localForm.progress) || 0,
        resourceHours:
          this.localForm.resourceHours === ""
            ? null
            : parseFloat(this.localForm.resourceHours),
        resourceCost:
          this.localForm.resourceCost === ""
            ? null
            : parseFloat(this.localForm.resourceCost),
        status: parseInt(this.localForm.status),
        startDate: this.localForm.startDate || null,
        dueDate: this.localForm.dueDate || null,
      });
    },
  },
};
</script>

<style scoped lang="scss">
@use "./grc-form.scss";
</style>
